// Employee self-service ("My HR") for the HR module of Khyati Portal / RSR International.
// A logged-in employee sees ONLY their own data: attendance, leave (apply), earnings,
// deductions, payslips and miss-punch corrections. All data comes from /hr/me/* endpoints.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  HRC, money, fmtDate, fmtTime, Card, SectionTitle, SubTabs, Th, Td, Table,
  Pill, Btn, Stat, Field, input, Modal, Empty, Loading,
} from './hrkit';

const today = () => new Date().toISOString().slice(0, 10);
const curMonth = () => new Date().toISOString().slice(0, 7);
const num = (v) => Number(v || 0);

// Day diff inclusive of both endpoints (Mon→Mon = 1, Mon→Tue = 2).
const dayDiff = (from, to) => {
  if (!from || !to) return 1;
  const a = new Date(from), b = new Date(to);
  if (isNaN(a) || isNaN(b)) return 1;
  const d = Math.round((b - a) / 86400000) + 1;
  return d > 0 ? d : 1;
};

const TYPE_COLOR = { monthly: HRC.navy, daily: HRC.amber, piece_rate: '#8B5CF6', contractor: HRC.green };
const isPiece = (t) => t === 'piece_rate';

const monthLabel = (m) => {
  if (!m) return '—';
  const d = new Date(m + (m.length === 7 ? '-01' : ''));
  if (isNaN(d)) return m;
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

// Reliever picker — loads ONLY employees genuinely free to cover [from,to].
// Reloads whenever the date range changes and clears any stale selection.
function RelieverPicker({ from_date, to_date, value, onChange }) {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!from_date || !to_date) { setOpts([]); return; }
    let alive = true;
    setLoading(true); setErr('');
    api.get('/hr/me/leave/relievers', { params: { from_date, to_date } })
      .then((r) => {
        if (!alive) return;
        const list = r.data?.data || [];
        setOpts(list);
        // Drop the selection if the chosen reliever is no longer eligible.
        if (value && !list.some((o) => o.id === value)) onChange('');
      })
      .catch((e) => { if (alive) { setErr(e.response?.data?.message || 'Could not load relievers'); setOpts([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from_date, to_date]);

  return (
    <Field label="Reliever (who will cover for you)">
      <select value={value} onChange={(e) => onChange(e.target.value)} style={input} disabled={loading || !opts.length}>
        <option value="">{loading ? 'Checking who is free…' : opts.length ? 'Select a reliever…' : 'No one is free for these dates'}</option>
        {opts.map((o) => (
          <option key={o.id} value={o.id}>{o.full_name} ({o.employee_code})</option>
        ))}
      </select>
      <div style={{ fontSize: 11, color: HRC.muted, marginTop: 4 }}>
        {err ? <span style={{ color: HRC.red }}>{err}</span>
          : 'Only colleagues with no clashing leave or cover-commitment are shown. They must accept before HR reviews your request.'}
      </div>
    </Field>
  );
}

// ───────────────────────────── Apply leave modal ─────────────────────────────
function ApplyLeaveModal({ onClose, onSaved }) {
  // Leave type is intentionally omitted while the leave policy is being finalised — staff
  // apply with dates + reason + reliever only; the backend defaults a placeholder type.
  const [form, setForm] = useState({
    from_date: today(), to_date: today(), days: 1, reason: '', reliever_id: '',
  });
  const [daysTouched, setDaysTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Changing dates also invalidates the reliever choice (different availability).
  const setDate = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v, reliever_id: '' };
    if (!daysTouched) next.days = dayDiff(next.from_date, next.to_date);
    return next;
  });

  const valid = form.from_date && form.to_date && num(form.days) > 0 && form.reliever_id;

  const submit = () => {
    if (!valid) { setErr('Please choose dates, days and a reliever.'); return; }
    setSaving(true); setErr('');
    api.post('/hr/me/leave', {
      from_date: form.from_date,
      to_date: form.to_date,
      days: num(form.days),
      reason: form.reason,
      reliever_id: form.reliever_id,
    })
      .then(() => onSaved())
      // Surface server errors verbatim — incl. the bidirectional block (409).
      .catch((e) => setErr(e.response?.data?.error || e.response?.data?.message || 'Could not submit leave request'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal title="Apply for Leave" width={540} onClose={onClose}>
      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 12, background: HRC.red + '12', padding: '8px 10px', borderRadius: 6 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="From">
          <input type="date" value={form.from_date} onChange={(e) => setDate('from_date', e.target.value)} style={input} />
        </Field>
        <Field label="To">
          <input type="date" value={form.to_date} min={form.from_date} onChange={(e) => setDate('to_date', e.target.value)} style={input} />
        </Field>

        <Field label="Days">
          <input type="number" min="0.5" step="0.5" value={form.days}
            onChange={(e) => { setDaysTouched(true); set('days', e.target.value); }} style={input} />
        </Field>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 9 }}>
          <span style={{ fontSize: 11, color: HRC.muted }}>Suggested: {dayDiff(form.from_date, form.to_date)} day(s)</span>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <RelieverPicker from_date={form.from_date} to_date={form.to_date}
            value={form.reliever_id} onChange={(v) => set('reliever_id', v)} />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Reason">
            <textarea value={form.reason} onChange={(e) => set('reason', e.target.value)} rows={3}
              placeholder="Reason for leave" style={{ ...input, resize: 'vertical' }} />
          </Field>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={saving} onClick={submit}>
          {saving ? 'Submitting…' : 'Submit Request'}
        </Btn>
      </div>
    </Modal>
  );
}

// ──────────────────── Withdraw a request still awaiting a reliever ───────────
// Rajan, 20 Oct 2026: "Nothing in this system may be unkillable." Cancel, never delete: the reason is required and
// kept with the request (mig 414). Once a reliever has accepted, the request is with HR and is not withdrawn here.
function WithdrawModal({ request, onClose, onSaved }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = () => {
    if (!reason.trim()) { setErr('Please say why you are withdrawing this request.'); return; }
    setSaving(true); setErr('');
    api.patch(`/hr/me/leave/${request.id}/cancel`, { reason: reason.trim() })
      .then(() => onSaved())
      .catch((e) => setErr(e.response?.data?.error || e.response?.data?.message || 'Could not withdraw the request'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal title="Withdraw this leave request" width={520} onClose={onClose}>
      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <div style={{ fontSize: 13, color: HRC.muted, marginBottom: 14 }}>
        {fmtDate(request.from_date)} – {fmtDate(request.to_date)} ({num(request.days)} day{num(request.days) === 1 ? '' : 's'}).
        It is kept, marked withdrawn, with your reason. A withdrawn request cannot be reopened; apply again if needed.
      </div>
      <Field label="Why are you withdrawing it?">
        <textarea style={{ ...input, minHeight: 70 }} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Keep it</Btn>
        <Btn variant="danger" disabled={saving} onClick={submit}>
          {saving ? 'Withdrawing…' : 'Withdraw request'}
        </Btn>
      </div>
    </Modal>
  );
}

// ──────────────────── Resend a declined request to a new reliever ────────────
function ResendModal({ request, onClose, onSaved }) {
  const [reliever_id, setReliever] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = () => {
    if (!reliever_id) { setErr('Please choose a reliever.'); return; }
    setSaving(true); setErr('');
    api.patch(`/hr/me/leave/${request.id}/reliever`, { reliever_id })
      .then(() => onSaved())
      .catch((e) => setErr(e.response?.data?.error || e.response?.data?.message || 'Could not resend request'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal title="Choose a different reliever" width={520} onClose={onClose}>
      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <div style={{ fontSize: 13, color: HRC.muted, marginBottom: 14 }}>
        {fmtDate(request.from_date)} – {fmtDate(request.to_date)} ({num(request.days)} day{num(request.days) === 1 ? '' : 's'})
      </div>
      <RelieverPicker from_date={request.from_date} to_date={request.to_date}
        value={reliever_id} onChange={setReliever} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={saving} onClick={submit}>
          {saving ? 'Resending…' : 'Resend Request'}
        </Btn>
      </div>
    </Modal>
  );
}

// ───────────────────────────── Correction modal ─────────────────────────────
function CorrectionModal({ initialDate, onClose, onSaved }) {
  const [form, setForm] = useState({
    work_date: initialDate || today(), requested_check_in: '', requested_check_out: '', reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.work_date && (form.requested_check_in || form.requested_check_out) && form.reason.trim();

  const submit = () => {
    if (!valid) { setErr('Provide the date, at least one punch time and a reason.'); return; }
    setSaving(true); setErr('');
    api.post('/hr/me/corrections', {
      work_date: form.work_date,
      request_type: 'miss_punch',
      requested_check_in: form.requested_check_in || null,
      requested_check_out: form.requested_check_out || null,
      reason: form.reason,
    })
      .then(() => onSaved())
      .catch((e) => setErr(e.response?.data?.message || 'Could not submit correction'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal title="Raise Miss-Punch Correction" width={520} onClose={onClose}>
      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 12 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Work Date">
            <input type="date" value={form.work_date} max={today()} onChange={(e) => set('work_date', e.target.value)} style={input} />
          </Field>
        </div>
        <Field label="Requested Check-in">
          <input type="time" value={form.requested_check_in} onChange={(e) => set('requested_check_in', e.target.value)} style={input} />
        </Field>
        <Field label="Requested Check-out">
          <input type="time" value={form.requested_check_out} onChange={(e) => set('requested_check_out', e.target.value)} style={input} />
        </Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Reason">
            <textarea value={form.reason} onChange={(e) => set('reason', e.target.value)} rows={3}
              placeholder="e.g. Forgot to punch out, machine was down…" style={{ ...input, resize: 'vertical' }} />
          </Field>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={saving} onClick={submit}>
          {saving ? 'Submitting…' : 'Submit Correction'}
        </Btn>
      </div>
    </Modal>
  );
}

// ───────────────────────────── My Attendance ─────────────────────────────
function AttendanceTab({ me }) {
  const [month, setMonth] = useState(curMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [fixDate, setFixDate] = useState(null); // opens correction modal

  const load = useCallback(() => {
    let alive = true;
    setLoading(true); setErr('');
    api.get('/hr/me/attendance', { params: { month } })
      .then((r) => { if (alive) setData(r.data?.data || null); })
      .catch((e) => { if (alive) { setErr(e.response?.data?.message || 'Could not load attendance'); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month]);

  useEffect(load, [load]);

  const piece = isPiece(me.employee_type);
  const s = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <Card>
      <SectionTitle right={
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          style={{ ...input, width: 'auto', padding: '7px 10px' }} />
      }>
        My Attendance — {monthLabel(month)}
      </SectionTitle>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <Loading /> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 16 }}>
            <Stat label="Present" value={num(s.present)} accent={HRC.green} />
            <Stat label="Absent" value={num(s.absent)} accent={HRC.red} />
            <Stat label="Leave" value={num(s.leave)} accent={HRC.navy} />
            <Stat label="Half Day" value={num(s.half_day)} accent={HRC.amber} />
            <Stat label="Late" value={num(s.late)} accent={HRC.amber} />
            <Stat label="Miss-Punch" value={num(s.miss)} accent={HRC.red} />
            <Stat label="OT Hours" value={num(s.ot_hours)} accent={'#8B5CF6'} />
            {piece && <Stat label="Units" value={num(s.units)} accent={'#8B5CF6'} />}
          </div>

          {rows.length === 0 ? <Empty>No attendance for {monthLabel(month)}</Empty> : (
            <Table>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Status</Th>
                  <Th>In</Th>
                  <Th>Out</Th>
                  <Th style={{ textAlign: 'right' }}>Worked</Th>
                  <Th style={{ textAlign: 'right' }}>OT</Th>
                  <Th style={{ textAlign: 'right' }}>Late min</Th>
                  <Th style={{ textAlign: 'right' }}>Units</Th>
                  <Th style={{ textAlign: 'right' }}></Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.work_date} style={r.is_miss_punch ? { background: HRC.red + '0F' } : undefined}>
                    <Td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {fmtDate(r.work_date)}
                      {r.is_miss_punch ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: HRC.red }}>MISS</span> : null}
                    </Td>
                    <Td><Pill value={r.status} /></Td>
                    <Td>{fmtTime(r.check_in)}</Td>
                    <Td>{fmtTime(r.check_out)}</Td>
                    <Td style={{ textAlign: 'right' }}>{num(r.worked_hours) || '—'}</Td>
                    <Td style={{ textAlign: 'right' }}>{num(r.ot_hours) || '—'}</Td>
                    <Td style={{ textAlign: 'right', color: num(r.late_minutes) ? HRC.amber : HRC.muted }}>{num(r.late_minutes) || '—'}</Td>
                    <Td style={{ textAlign: 'right' }}>{piece ? (num(r.units_produced) || '—') : '—'}</Td>
                    <Td style={{ textAlign: 'right' }}>
                      {r.is_miss_punch && (
                        <Btn size="sm" variant="soft" onClick={() => setFixDate(r.work_date)}>Fix</Btn>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}

      {fixDate && (
        <CorrectionModal
          initialDate={fixDate}
          onClose={() => setFixDate(null)}
          onSaved={() => { setFixDate(null); load(); }}
        />
      )}
    </Card>
  );
}

// ───────────────────────────── My Leave ─────────────────────────────
function LeaveTab() {
  const year = new Date().getFullYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showApply, setShowApply] = useState(false);
  const [resend, setResend] = useState(null); // declined request being reassigned
  const [withdraw, setWithdraw] = useState(null); // request awaiting a reliever, being withdrawn

  const load = useCallback(() => {
    let alive = true;
    setLoading(true); setErr('');
    api.get('/hr/me/leave', { params: { year } })
      .then((r) => { if (alive) setData(r.data?.data || null); })
      .catch((e) => { if (alive) { setErr(e.response?.data?.message || 'Could not load leave'); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [year]);

  useEffect(load, [load]);

  const requests = data?.requests || [];

  return (
    <Card>
      <SectionTitle right={
        <Btn variant="primary" size="sm" onClick={() => setShowApply(true)}>+ Apply Leave</Btn>
      }>
        My Leave — {year}
      </SectionTitle>

      {/* Always-visible notice while the leave policy is being finalised. */}
      <div style={{ background: '#EAF2FB', border: `1px solid #B6D4F2`, color: '#1B4F8A', borderRadius: 8,
        padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16 }} aria-hidden>ℹ️</span>
        LEAVE POLICY IS IN MAKING, PLS CONTACT HR DEPTT FOR THE SAME
      </div>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <Loading /> : (
        <>
          {/* Leave balances are intentionally hidden while the leave policy is being finalised. */}
          <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 700, marginBottom: 8 }}>My Requests</div>
          {requests.length === 0 ? <Empty>No leave requests yet</Empty> : (
            <Table>
              <thead>
                <tr>
                  <Th>From–To</Th>
                  <Th style={{ textAlign: 'right' }}>Days</Th>
                  <Th>Reliever</Th>
                  <Th>Status</Th>
                  <Th>Applied</Th>
                  <Th style={{ textAlign: 'right' }}></Th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id}>
                    <Td style={{ whiteSpace: 'nowrap' }}>
                      {fmtDate(r.from_date)} – {fmtDate(r.to_date)}
                      {r.half_day ? <div style={{ fontSize: 10, color: HRC.amber, fontWeight: 700 }}>Half day</div> : null}
                    </Td>
                    <Td style={{ textAlign: 'right', fontWeight: 600 }}>{num(r.days)}</Td>
                    <Td>
                      {r.reliever_name
                        ? <span style={{ fontSize: 12 }}>{r.reliever_name}
                            {r.reliever_status ? <span style={{ fontSize: 10, color: HRC.muted, marginLeft: 6 }}>({String(r.reliever_status)})</span> : null}
                          </span>
                        : <span style={{ color: HRC.muted }}>—</span>}
                    </Td>
                    <Td><Pill value={r.status} /></Td>
                    <Td style={{ whiteSpace: 'nowrap', color: HRC.muted, fontSize: 12 }}>{fmtDate(r.created_at)}</Td>
                    <Td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                        {r.status === 'declined_by_reliever' && (
                          <Btn size="sm" variant="soft" onClick={() => setResend(r)}>Pick new reliever</Btn>
                        )}
                        {(r.status === 'pending_reliever' || r.status === 'declined_by_reliever') && (
                          <Btn size="sm" variant="ghost" onClick={() => setWithdraw(r)}>Withdraw</Btn>
                        )}
                      </div>
                      {r.status === 'cancelled' && r.cancel_reason
                        ? <div style={{ fontSize: 11, color: HRC.muted, fontStyle: 'italic' }}>“{r.cancel_reason}”</div> : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}

      {showApply && (
        <ApplyLeaveModal
          onClose={() => setShowApply(false)}
          onSaved={() => { setShowApply(false); load(); }}
        />
      )}

      {withdraw && (
        <WithdrawModal request={withdraw}
          onClose={() => setWithdraw(null)}
          onSaved={() => { setWithdraw(null); load(); }}
        />
      )}

      {resend && (
        <ResendModal
          request={resend}
          onClose={() => setResend(null)}
          onSaved={() => { setResend(null); load(); }}
        />
      )}
    </Card>
  );
}

// ───────────────────────────── Reliever Requests ─────────────────────────────
// Consent requests where I'm the named reliever. Accept (I commit to cover and
// it moves to HR) or Decline (the applicant must pick someone else).
function RelieverRequestsTab({ onCountChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true); setErr('');
    api.get('/hr/me/leave/reliever-requests')
      .then((r) => { if (alive) { const d = r.data?.data || []; setRows(d); onCountChange?.(d.length); } })
      .catch((e) => { if (alive) { setErr(e.response?.data?.message || 'Could not load reliever requests'); setRows([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [onCountChange]);

  useEffect(load, [load]);

  const respond = (id, decision) => {
    setBusy(id); setErr('');
    api.patch(`/hr/me/leave/reliever-requests/${id}`, { decision })
      .then(load)
      .catch((e) => setErr(e.response?.data?.error || e.response?.data?.message || 'Could not save your response'))
      .finally(() => setBusy(null));
  };

  return (
    <Card>
      <SectionTitle>Reliever Requests</SectionTitle>
      <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 12 }}>
        Colleagues who have named you as their reliever. If you accept, you commit to cover for them and
        cannot take your own leave over those dates.
      </div>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <Loading /> : rows.length === 0 ? <Empty>No pending reliever requests</Empty> : (
        <Table>
          <thead>
            <tr>
              <Th>Applicant</Th>
              <Th>From–To</Th>
              <Th style={{ textAlign: 'right' }}>Days</Th>
              <Th>Reason</Th>
              <Th style={{ textAlign: 'right' }}>Action</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <Td>
                  <div style={{ fontWeight: 600 }}>{r.applicant_name || '—'}</div>
                  <div style={{ fontSize: 11, color: HRC.muted }}>{r.applicant_code || ''}</div>
                </Td>
                <Td style={{ whiteSpace: 'nowrap' }}>
                  {fmtDate(r.from_date)} – {fmtDate(r.to_date)}
                  {r.half_day ? <div style={{ fontSize: 10, color: HRC.amber, fontWeight: 700 }}>Half day</div> : null}
                </Td>
                <Td style={{ textAlign: 'right', fontWeight: 600 }}>{num(r.days)}</Td>
                <Td style={{ maxWidth: 220, color: HRC.muted }}><span title={r.reason || ''}>{r.reason || '—'}</span></Td>
                <Td style={{ textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                    <Btn size="sm" variant="primary" style={{ background: HRC.green }} disabled={busy === r.id}
                      onClick={() => respond(r.id, 'accepted')}>Accept</Btn>
                    <Btn size="sm" variant="danger" disabled={busy === r.id}
                      onClick={() => respond(r.id, 'declined')}>Decline</Btn>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// ───────────────────────────── My Earnings ─────────────────────────────
function EarningsTab({ me }) {
  const [month, setMonth] = useState(curMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    let alive = true;
    setLoading(true); setErr('');
    api.get('/hr/me/earnings', { params: { month } })
      .then((r) => { if (alive) setData(r.data?.data || null); })
      .catch((e) => { if (alive) { setErr(e.response?.data?.message || 'Could not load earnings'); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month]);

  useEffect(load, [load]);

  const piece = isPiece(me.employee_type);
  const d = data || {};

  return (
    <Card>
      <SectionTitle right={
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          style={{ ...input, width: 'auto', padding: '7px 10px' }} />
      }>
        My Earnings — {monthLabel(month)}
      </SectionTitle>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <Loading /> : !data ? <Empty>No earnings for {monthLabel(month)}</Empty> : (
        <>
          <div style={{ background: HRC.navy, borderRadius: 12, padding: '20px 22px', color: '#fff', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.85 }}>Tentative Gross Earnings</div>
            <div style={{ fontSize: 34, fontWeight: 800, marginTop: 4 }}>{money(d.gross_earnings)}</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
              Tentative as of {fmtDate(d.upto)}{d.method ? ` · ${d.method}` : ''}
            </div>
            {piece && <div style={{ fontSize: 12, marginTop: 6, fontWeight: 600, color: '#FFD9D9' }}>Piece-rate: earnings are based on your output (units produced).</div>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
            <Stat label="Basic" value={money(d.basic)} accent={HRC.navy} />
            <Stat label="Allowances" value={money(d.allowances)} accent={HRC.navy} />
            <Stat label="Overtime" value={money(d.overtime_amount)} sub={`${num(d.ot_hours)} hrs`} accent={'#8B5CF6'} />
            {piece && <Stat label="Piece Earnings" value={money(d.piece_earnings)} sub={`${num(d.units)} units`} accent={'#8B5CF6'} />}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
            <Stat label="Days in Month" value={num(d.days_in_month)} accent={HRC.muted} />
            <Stat label="Present Days" value={num(d.present_days)} accent={HRC.green} />
            <Stat label="Paid Days" value={num(d.paid_days)} accent={HRC.green} />
            <Stat label="LOP Days" value={num(d.lop_days)} accent={HRC.red} />
            <Stat label="OT Hours" value={num(d.ot_hours)} accent={'#8B5CF6'} />
            {piece && <Stat label="Units" value={num(d.units)} accent={'#8B5CF6'} />}
          </div>
        </>
      )}
    </Card>
  );
}

// ───────────────────────────── Deductions ─────────────────────────────
function DeductionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr('');
    api.get('/hr/me/deductions')
      .then((r) => { if (alive) setData(r.data?.data || null); })
      .catch((e) => { if (alive) { setErr(e.response?.data?.message || 'Could not load deductions'); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const loans = data?.loans || [];
  const payslips = data?.payslips || [];

  return (
    <Card>
      <SectionTitle>Loans, Advances &amp; Deductions</SectionTitle>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <Loading /> : (
        <>
          <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 700, marginBottom: 8 }}>Loans &amp; Advances</div>
          {loans.length === 0 ? <Empty>No active loans or advances</Empty> : (
            <Table>
              <thead>
                <tr>
                  <Th>Type</Th>
                  <Th style={{ textAlign: 'right' }}>Principal</Th>
                  <Th style={{ textAlign: 'right' }}>Installment/mo</Th>
                  <Th style={{ textAlign: 'right' }}>Outstanding</Th>
                  <Th>Status</Th>
                  <Th>Disbursed</Th>
                  <Th>Reason</Th>
                </tr>
              </thead>
              <tbody>
                {loans.map((l, i) => (
                  <tr key={i}>
                    <Td style={{ fontWeight: 600 }}>{l.loan_type || '—'}</Td>
                    <Td style={{ textAlign: 'right' }}>{money(l.principal)}</Td>
                    <Td style={{ textAlign: 'right' }}>{money(l.installment_amount)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 700, color: HRC.red }}>{money(l.outstanding)}</Td>
                    <Td><Pill value={l.status} /></Td>
                    <Td style={{ whiteSpace: 'nowrap', color: HRC.muted, fontSize: 12 }}>{fmtDate(l.disbursed_date)}</Td>
                    <Td style={{ maxWidth: 200, color: HRC.muted }}><span title={l.reason || ''}>{l.reason || '—'}</span></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 700, margin: '20px 0 8px' }}>Recent Payslip Deductions</div>
          {payslips.length === 0 ? <Empty>No payslip deductions yet</Empty> : (
            <Table>
              <thead>
                <tr>
                  <Th>Period</Th>
                  <Th style={{ textAlign: 'right' }}>Gross</Th>
                  <Th style={{ textAlign: 'right' }}>Loan</Th>
                  <Th style={{ textAlign: 'right' }}>Advance</Th>
                  <Th style={{ textAlign: 'right' }}>Total Ded.</Th>
                  <Th style={{ textAlign: 'right' }}>Net Pay</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {payslips.map((p, i) => (
                  <tr key={i}>
                    <Td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{monthLabel(p.period_month)}</Td>
                    <Td style={{ textAlign: 'right' }}>{money(p.gross_earnings)}</Td>
                    <Td style={{ textAlign: 'right' }}>{money(p.loan_deduction)}</Td>
                    <Td style={{ textAlign: 'right' }}>{money(p.advance_deduction)}</Td>
                    <Td style={{ textAlign: 'right', color: HRC.red }}>{money(p.total_deductions)}</Td>
                    <Td style={{ textAlign: 'right', fontWeight: 700, color: HRC.green }}>{money(p.net_pay)}</Td>
                    <Td><Pill value={p.status} /></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Card>
  );
}

// ───────────────────────────── Payslips ─────────────────────────────
function PayslipsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr('');
    api.get('/hr/me/payslips')
      .then((r) => { if (alive) setRows(r.data?.data || []); })
      .catch((e) => { if (alive) { setErr(e.response?.data?.message || 'Could not load payslips'); setRows([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <Card>
      <SectionTitle>My Payslips</SectionTitle>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <Loading /> : rows.length === 0 ? <Empty>No payslips available</Empty> : (
        <Table>
          <thead>
            <tr>
              <Th>Period</Th>
              <Th style={{ textAlign: 'right' }}>Gross</Th>
              <Th style={{ textAlign: 'right' }}>Deductions</Th>
              <Th style={{ textAlign: 'right' }}>Net Pay</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <Td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{monthLabel(p.period_month)}</Td>
                <Td style={{ textAlign: 'right' }}>{money(p.gross_earnings)}</Td>
                <Td style={{ textAlign: 'right', color: HRC.red }}>{money(p.total_deductions)}</Td>
                <Td style={{ textAlign: 'right', fontWeight: 700, color: HRC.green }}>{money(p.net_pay)}</Td>
                <Td><Pill value={p.status || p.run_status} /></Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// ───────────────────────────── Corrections ─────────────────────────────
function CorrectionsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [show, setShow] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true); setErr('');
    api.get('/hr/me/corrections')
      .then((r) => { if (alive) setRows(r.data?.data || []); })
      .catch((e) => { if (alive) { setErr(e.response?.data?.message || 'Could not load corrections'); setRows([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(load, [load]);

  return (
    <Card>
      <SectionTitle right={
        <Btn variant="primary" size="sm" onClick={() => setShow(true)}>+ Raise Miss-Punch Correction</Btn>
      }>
        My Correction Requests
      </SectionTitle>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {loading ? <Loading /> : rows.length === 0 ? <Empty>No correction requests yet</Empty> : (
        <Table>
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Requested In</Th>
              <Th>Requested Out</Th>
              <Th>Reason</Th>
              <Th>Status</Th>
              <Th>Decision</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <Td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtDate(c.work_date)}</Td>
                <Td>{fmtTime(c.requested_check_in)}</Td>
                <Td>{fmtTime(c.requested_check_out)}</Td>
                <Td style={{ maxWidth: 220, color: HRC.muted }}><span title={c.reason || ''}>{c.reason || '—'}</span></Td>
                <Td><Pill value={c.status} /></Td>
                <Td style={{ maxWidth: 200, fontSize: 12, color: HRC.muted }}>
                  {c.decision_note ? <span style={{ fontStyle: 'italic' }}>“{c.decision_note}”</span> : '—'}
                  {c.reviewed_at ? <div>{fmtDate(c.reviewed_at)}</div> : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {show && (
        <CorrectionModal
          onClose={() => setShow(false)}
          onSaved={() => { setShow(false); load(); }}
        />
      )}
    </Card>
  );
}

// ───────────────────────────── Entry ─────────────────────────────
export default function MyHR() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noProfile, setNoProfile] = useState(false);
  const [tab, setTab] = useState('attendance');
  const [relieverCount, setRelieverCount] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/hr/me')
      .then((r) => { if (alive) setMe(r.data?.data || null); })
      // 401 is auto-handled by the api interceptor; any other error (incl. 404) means no linked profile.
      .catch(() => { if (alive) setNoProfile(true); })
      .finally(() => { if (alive) setLoading(false); });
    // Pending reliever-consent count for the tab badge.
    const refreshCount = () => api.get('/hr/me/leave/reliever-requests')
      .then((r) => { if (alive) setRelieverCount((r.data?.data || []).length); })
      .catch(() => {});
    refreshCount();
    // Live bump when a new reliever request arrives over the socket.
    window.addEventListener('hr:reliever_request', refreshCount);
    return () => { alive = false; window.removeEventListener('hr:reliever_request', refreshCount); };
  }, []);

  const TABS = [
    { key: 'attendance', label: 'My Attendance' },
    { key: 'leave', label: 'My Leave' },
    { key: 'reliever', label: relieverCount ? `Reliever Requests (${relieverCount})` : 'Reliever Requests' },
    { key: 'earnings', label: 'My Earnings' },
    { key: 'deductions', label: 'Deductions' },
    { key: 'payslips', label: 'Payslips' },
    { key: 'corrections', label: 'Corrections' },
  ];

  if (loading) {
    return <div style={{ padding: 24 }}><Card><Loading /></Card></div>;
  }

  if (noProfile || !me) {
    return (
      <div style={{ padding: 24, maxWidth: 560, margin: '0 auto' }}>
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🙍</div>
          <h3 style={{ margin: '0 0 8px', color: HRC.text, fontSize: 17 }}>No employee profile linked</h3>
          <div style={{ fontSize: 13, color: HRC.muted, lineHeight: 1.5 }}>
            No employee profile is linked to your account.<br />Please contact HR.
          </div>
        </Card>
      </div>
    );
  }

  const typeColor = TYPE_COLOR[me.employee_type] || HRC.navy;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      {/* Profile header */}
      <Card style={{ marginBottom: 16, borderLeft: `4px solid ${HRC.navy}` }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%', background: HRC.violet, color: HRC.navy,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20,
            }}>
              {String(me.full_name || '?').trim().charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: HRC.text }}>{me.full_name || '—'}</div>
              <div style={{ fontSize: 12, color: HRC.muted, marginTop: 2 }}>
                {me.employee_code || '—'}
                {(me.department_name || me.designation) ? ' · ' : ''}
                {[me.department_name, me.designation].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            {me.employee_type && (
              <span style={{
                display: 'inline-block', padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                color: typeColor, background: typeColor + '1A', textTransform: 'capitalize',
              }}>{String(me.employee_type).replace(/_/g, ' ')}</span>
            )}
            {me.status && <Pill value={me.status} />}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Joined</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: HRC.text }}>{fmtDate(me.date_of_joining)}</div>
            </div>
            {(me.shift_start || me.shift_end) && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Shift</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: HRC.text }}>{fmtTime(me.shift_start)} – {fmtTime(me.shift_end)}</div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <SubTabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'attendance' && <AttendanceTab me={me} />}
      {tab === 'leave' && <LeaveTab />}
      {tab === 'reliever' && <RelieverRequestsTab onCountChange={setRelieverCount} />}
      {tab === 'earnings' && <EarningsTab me={me} />}
      {tab === 'deductions' && <DeductionsTab />}
      {tab === 'payslips' && <PayslipsTab />}
      {tab === 'corrections' && <CorrectionsTab />}
    </div>
  );
}
